/**
 * Kokoushuoneiden hallinta (auth).
 *
 * - Luo uusi kokous (saadaan koodi + jaettava linkki)
 * - Listaa omat kokoukset
 * - Kopioi/avaa linkki
 * - Liity itse → /meet/<code>
 * - Poista kokous
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Video, Plus, Copy, Trash2, LogIn, Loader2, Calendar, Link2, X, RefreshCw, LifeBuoy, Lock, HeartHandshake, Users } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/browser";
import { ModuleSettingsButton } from "@/components/admin/FactoryResetDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";

export default function MeetingDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", scheduled_at: "", duration_minutes: 60, max_participants: 10 });
  const [saving, setSaving] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState(null);
  const [invitesFor, setInvitesFor] = useState(null);     // active meeting object with .id, .room_code
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({ label: "", guest_name_prefill: "", max_uses: 1, expires_in_minutes: 1440, allow_rejoin: true });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [supportDialog, setSupportDialog] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportSending, setSupportSending] = useState(false);
  const [licenseError, setLicenseError] = useState(false);

  // Lisenssi & oikeudet
  // hasMeetingLicense vastaa backendin _owner_has_meeting_license tarkistusta:
  //  - admin aina (privilegoidut)
  //  - muut: vain jos tilin moduuleissa on 'meeting'
  // license_customer joka EI omista 'meeting' moduulia näkee no-license-cardin
  // (vastaa backendin POST /meetings 403:a → ei UX-epäjohdonmukaisuutta).
  const isAdmin = user?.role === "admin";
  const isLicenseAdmin = user?.role === "license_customer";
  const hasMeetingLicense = isAdmin || (user?.modules || []).includes("meeting");
  // canCreateInvites: oikeus luoda ulkoisia kutsulinkkejä.
  //  - admin: aina
  //  - license_customer + lisenssi: aina
  //  - staff: tarvitsee erillisen lipun + lisenssin
  const canCreateInvites =
    isAdmin || (hasMeetingLicense && (isLicenseAdmin || !!user?.can_create_external_invites));

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/meetings");
      setList(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) {
      toast.error("Anna kokoukselle otsikko");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/meetings", {
        ...form,
        duration_minutes: parseInt(form.duration_minutes) || 60,
        max_participants: parseInt(form.max_participants) || 10,
        scheduled_at: form.scheduled_at || null,
      });
      setShowForm(false);
      setForm({ title: "", description: "", scheduled_at: "", duration_minutes: 60, max_participants: 10 });
      setCreatedMeeting(r.data);
      load();
    } catch (e) {
      if (e?.response?.status === 403) {
        setLicenseError(true);
      }
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const sendSupportRequest = async () => {
    setSupportSending(true);
    try {
      const r = await api.post("/meetings/support-request", { message: supportMessage.trim() || null });
      toast.success(r.data.message || "Tukipyyntö lähetetty");
      setSupportDialog(false);
      setSupportMessage("");
      // Avaa kutsulinkki suoraan
      if (r.data.join_url_path) {
        window.open(r.data.join_url_path, "_blank");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSupportSending(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko kokous?")) return;
    try {
      await api.delete(`/meetings/${id}`);
      toast.success("Poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async (code) => {
    const url = `${window.location.origin}/meet/${code}`;
    const ok = await copyToClipboard(url);
    toast[ok ? "success" : "info"](ok ? "Linkki kopioitu" : `Linkki: ${url}`);
  };

  // ------------- Invite-hallinta (kertakäyttölinkit) -------------
  const openInvites = async (meeting) => {
    setInvitesFor(meeting);
    setInvitesLoading(true);
    try {
      const r = await api.get(`/meetings/${meeting.id}/invites`);
      setInvites(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setInvitesLoading(false);
  };
  const closeInvites = () => {
    setInvitesFor(null);
    setInvites([]);
    setInviteForm({ label: "", guest_name_prefill: "", max_uses: 1, expires_in_minutes: 1440, allow_rejoin: true });
  };
  const createInvite = async () => {
    if (!invitesFor) return;
    setInviteSaving(true);
    try {
      const r = await api.post(`/meetings/${invitesFor.id}/invites`, {
        ...inviteForm,
        max_uses: parseInt(inviteForm.max_uses) || 1,
        expires_in_minutes: parseInt(inviteForm.expires_in_minutes) || 1440,
      });
      setInvites([r.data, ...invites]);
      setInviteForm({ label: "", guest_name_prefill: "", max_uses: 1, expires_in_minutes: 1440, allow_rejoin: true });
      toast.success("Kutsulinkki luotu");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setInviteSaving(false);
  };
  const revokeInvite = async (inv) => {
    if (!window.confirm(`Peruuta kutsu "${inv.label || inv.token.slice(0, 8)}"? Vanha linkki lakkaa toimimasta.`)) return;
    try {
      const r = await api.delete(`/meetings/invites/${inv.id}`);
      setInvites(invites.map(i => i.id === inv.id ? r.data : i));
      toast.success("Kutsu peruttu");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const copyInviteLink = async (inv) => {
    const url = `${window.location.origin}/meet/invite/${inv.token}`;
    const ok = await copyToClipboard(url);
    toast[ok ? "success" : "info"](ok ? "Kutsulinkki kopioitu" : `Linkki: ${url}`);
  };

  return (
    <div className="space-y-6" data-testid="meeting-dashboard">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-sky-600 flex items-center justify-center shadow-lg">
            <Video className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Moduuli</p>
            <h1 className="text-3xl font-display gradient-text-luxe">Kokoushuone</h1>
            <p className="text-white/60 text-sm mt-1">
              Selain-pohjaiset videoneuvottelut (WebRTC P2P): kamera, ääni, näytönjako, chat. Ilmainen, ei rajoitusta kokousajalle.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSupportDialog(true)} className="border-pink-400/30 text-pink-200 inline-flex items-center gap-2" data-testid="support-request-button">
            <LifeBuoy className="w-4 h-4" /> Pyydä tukea
          </Button>
          {hasMeetingLicense && (
            <>
              <ModuleSettingsButton moduleKey="meeting" moduleLabel="Kokoushuone" onAfterReset={load} />
              <Button onClick={() => setShowForm(true)} className="btn-luxe inline-flex items-center gap-2" data-testid="meeting-new-button">
                <Plus className="w-4 h-4" /> Uusi kokous
              </Button>
            </>
          )}
        </div>
      </header>

      {/* Tabs: Kokoukset vs Henkilökunta */}
      <Tabs defaultValue="meetings" className="space-y-4">
        <TabsList className="bg-white/[0.04] border border-white/5">
          <TabsTrigger value="meetings" data-testid="meeting-tab-meetings">Kokoukset</TabsTrigger>
          <TabsTrigger value="staff" data-testid="meeting-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
        </TabsList>

        <TabsContent value="meetings" className="space-y-4">

      {/* Ilman lisenssiä: näytä iso "Pyydä tukea" -kortti */}
      {!hasMeetingLicense && (
        <div className="glass-card p-8 text-center space-y-4" data-testid="no-license-card">
          <div className="w-16 h-16 mx-auto rounded-full bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
            <Lock className="w-8 h-8 text-pink-300" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-pink-300/80">Kokoushuone-moduuli ei käytössä</p>
            <h2 className="text-2xl font-display text-white mt-1">Tarvitsetko apua?</h2>
            <p className="text-white/60 text-sm mt-2 max-w-md mx-auto">
              Kokoushuone-moduulia ei ole aktivoitu tilillesi. Voit silti ottaa yhteyttä Apps2Go-tukeen ja saat
              kutsulinkin yhden klikkauksen päässä – pääkäyttäjä liittyy mahdollisimman pian.
            </p>
          </div>
          <Button onClick={() => setSupportDialog(true)} className="btn-luxe inline-flex items-center gap-2 mx-auto" data-testid="support-request-large">
            <HeartHandshake className="w-4 h-4" /> Ota yhteyttä pääkäyttäjään
          </Button>
        </div>
      )}

      {/* Quick instant meeting – vain lisenssin omaavalle */}
      {hasMeetingLicense && (
      <div className="glass-card p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-white/80">Pikakokous nyt? Luo huone yhdellä klikkauksella.</p>
          <p className="text-xs text-white/40 mt-0.5">Saat välittömästi linkin jaettavaksi.</p>
        </div>
        <Button
          variant="outline"
          onClick={async () => {
            try {
              const r = await api.post("/meetings", { title: "Pikakokous", duration_minutes: 60, max_participants: 10 });
              setCreatedMeeting(r.data);
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          className="border-amber-500/30 text-amber-200"
          data-testid="meeting-instant-button"
        >
          <Video className="w-4 h-4 mr-2" /> Aloita pikakokous
        </Button>
      </div>
      )}

      {/* List – vain lisenssin omaavalle */}
      {hasMeetingLicense && (
      <div className="glass-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" /> Ladataan...
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-white/40">
            <Video className="w-10 h-10 mx-auto mb-3 text-white/20" />
            <p>Ei kokouksia. Luo ensimmäinen.</p>
          </div>
        ) : (
          <table className="w-full text-sm relative z-10">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                <th className="py-3 px-4">Aihe</th>
                <th className="py-3 px-4">Koodi</th>
                <th className="py-3 px-4">Aikataulu</th>
                <th className="py-3 px-4">Kesto</th>
                <th className="py-3 px-4 text-right w-72"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`meeting-row-${m.id}`}>
                  <td className="py-3 px-4">
                    <p className="text-white font-medium">{m.title}</p>
                    {m.description && <p className="text-[11px] text-white/40 mt-0.5">{m.description}</p>}
                  </td>
                  <td className="py-3 px-4">
                    <code className="text-amber-300 text-xs font-mono bg-amber-500/10 px-2 py-1 rounded">{m.room_code}</code>
                  </td>
                  <td className="py-3 px-4 text-white/70 text-xs">
                    {m.scheduled_at ? new Date(m.scheduled_at).toLocaleString("fi-FI") : "Ei aikataulua"}
                  </td>
                  <td className="py-3 px-4 text-white/70 text-xs">{m.duration_minutes} min</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2 justify-end">
                      {canCreateInvites && (
                        <Button size="sm" variant="outline" onClick={() => openInvites(m)} className="inline-flex items-center gap-1 border-cyan-400/30 text-cyan-200" data-testid={`meeting-invites-${m.id}`}>
                          <Link2 className="w-3 h-3" /> Kutsulinkit
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => copyLink(m.room_code)} className="inline-flex items-center gap-1" data-testid={`meeting-copy-${m.id}`}>
                        <Copy className="w-3 h-3" /> Linkki
                      </Button>
                      <Button size="sm" className="btn-luxe inline-flex items-center gap-1" onClick={() => navigate(`/meet/${m.room_code}`)} data-testid={`meeting-join-${m.id}`}>
                        <LogIn className="w-3 h-3" /> Liity
                      </Button>
                      <Button size="icon" variant="ghost" className="w-7 h-7 text-red-300" onClick={() => remove(m.id)} data-testid={`meeting-delete-${m.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

        </TabsContent>

        <TabsContent value="staff">
          <ModuleStaffTab moduleKey="meeting" moduleLabel="Kokoushuone" />
        </TabsContent>
      </Tabs>

      {/* Create dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Uusi kokous</DialogTitle>
            <DialogDescription>
              Luotuasi kokouksen saat jaettavan linkin, jolla kuka tahansa pääsee mukaan ilman tunnusta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Otsikko</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="esim. Viikkopalaveri" data-testid="meeting-form-title" />
            </div>
            <div>
              <Label className="text-xs">Kuvaus</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ajankohta</Label>
                <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Kesto (min)</Label>
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Max. osallistujia</Label>
              <Input type="number" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} />
              <p className="text-[10px] text-white/40 mt-1">Suositus 2–6 (P2P-yhteys). Suurempi vaatii TURN-välityspalvelimen.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowForm(false)} className="flex-1">Peruuta</Button>
              <Button onClick={create} disabled={saving} className="btn-luxe flex-1" data-testid="meeting-form-save">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Luo & saa linkki
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Created link dialog */}
      <Dialog open={!!createdMeeting} onOpenChange={(v) => !v && setCreatedMeeting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kokoushuone valmis</DialogTitle>
            <DialogDescription>
              Jaa linkki osallistujille tai liity itse heti.
            </DialogDescription>
          </DialogHeader>
          {createdMeeting && (
            <div className="space-y-3">
              <p className="text-white text-lg font-semibold">{createdMeeting.title}</p>
              <p className="text-white/60 text-xs">Huoneen koodi: <code className="text-amber-300">{createdMeeting.room_code}</code></p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={`${window.location.origin}/meet/${createdMeeting.room_code}`}
                  onClick={(e) => e.target.select()}
                  className="font-mono text-xs"
                  data-testid="meeting-created-url"
                />
                <Button
                  onClick={async () => {
                    const ok = await copyToClipboard(`${window.location.origin}/meet/${createdMeeting.room_code}`);
                    toast[ok ? "success" : "info"](ok ? "Kopioitu" : "Kopiointi estetty");
                  }}
                  className="btn-luxe"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => {
                  const mailto = `mailto:?subject=${encodeURIComponent(createdMeeting.title)}&body=${encodeURIComponent(`Liity kokoukseen:\n${window.location.origin}/meet/${createdMeeting.room_code}\n\nApps2Go Hub`)}`;
                  window.location.href = mailto;
                }}>Lähetä sähköpostilla</Button>
                <Button className="btn-luxe flex-1" onClick={() => navigate(`/meet/${createdMeeting.room_code}`)} data-testid="meeting-created-join">
                  <LogIn className="w-4 h-4 mr-1" /> Liity heti
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Invite-linkkien hallinta */}
      <Dialog open={!!invitesFor} onOpenChange={(v) => !v && closeInvites()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-cyan-300" />
              Kutsulinkit · {invitesFor?.title}
            </DialogTitle>
            <DialogDescription>
              Luo kertakäyttöisiä kutsulinkkejä ulkopuolisille. Pudotuksen jälkeen sama vieras voi liittyä uudelleen.
            </DialogDescription>
          </DialogHeader>

          {/* Uuden kutsun lomake */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3 mb-4">
            <p className="text-xs uppercase tracking-widest text-cyan-300/80">Uusi kutsulinkki</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-white/60">Nimitunniste</Label>
                <Input
                  value={inviteForm.label}
                  onChange={(e) => setInviteForm({ ...inviteForm, label: e.target.value })}
                  placeholder="esim. Acme Oy"
                  data-testid="invite-label"
                />
              </div>
              <div>
                <Label className="text-xs text-white/60">Esitäytetty nimi (valinnainen)</Label>
                <Input
                  value={inviteForm.guest_name_prefill}
                  onChange={(e) => setInviteForm({ ...inviteForm, guest_name_prefill: e.target.value })}
                  placeholder="esim. Maija Asiakas"
                  data-testid="invite-prefill"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-white/60">Käyttömäärä</Label>
                <select
                  value={inviteForm.max_uses}
                  onChange={(e) => setInviteForm({ ...inviteForm, max_uses: parseInt(e.target.value) })}
                  className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  data-testid="invite-max-uses"
                >
                  <option value="1">1 (kertakäyttö)</option>
                  <option value="2">2</option>
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="0">Rajaton</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-white/60">Vanhenee</Label>
                <select
                  value={inviteForm.expires_in_minutes}
                  onChange={(e) => setInviteForm({ ...inviteForm, expires_in_minutes: parseInt(e.target.value) })}
                  className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  data-testid="invite-expires"
                >
                  <option value="60">1 tunti</option>
                  <option value="240">4 tuntia</option>
                  <option value="1440">24 tuntia</option>
                  <option value="10080">7 vrk</option>
                  <option value="43200">30 vrk</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inviteForm.allow_rejoin}
                    onChange={(e) => setInviteForm({ ...inviteForm, allow_rejoin: e.target.checked })}
                    data-testid="invite-allow-rejoin"
                  />
                  Salli rejoin (suositus)
                </label>
              </div>
            </div>
            <Button
              onClick={createInvite}
              disabled={inviteSaving}
              className="btn-luxe w-full inline-flex items-center justify-center gap-2"
              data-testid="invite-create"
            >
              {inviteSaving && <Loader2 className="w-3 h-3 animate-spin" />}
              <Plus className="w-3.5 h-3.5" /> Luo kutsulinkki
            </Button>
          </div>

          {/* Olemassa olevat */}
          <div className="flex-1 overflow-y-auto space-y-2" data-testid="invites-list">
            <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Olemassa olevat ({invites.length})</p>
            {invitesLoading ? (
              <div className="flex items-center gap-2 text-white/40 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Ladataan…</div>
            ) : invites.length === 0 ? (
              <p className="text-xs text-white/30 italic text-center py-6">Ei kutsulinkkejä</p>
            ) : (
              invites.map(inv => {
                const expired = inv.expires_at && new Date(inv.expires_at) < new Date();
                const exhausted = inv.max_uses > 0 && inv.used_count >= inv.max_uses;
                const dead = !inv.is_active || expired || exhausted;
                return (
                  <div key={inv.id} className={`rounded-lg border p-3 ${dead ? "border-white/5 bg-white/[0.01] opacity-60" : "border-white/10 bg-white/[0.02]"}`} data-testid={`invite-row-${inv.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white font-medium truncate">
                          {inv.label || <span className="italic text-white/40">Nimetön</span>}
                        </p>
                        <p className="text-[10px] text-white/40 font-mono break-all">
                          /meet/invite/{inv.token.slice(0, 16)}…
                        </p>
                        <div className="flex flex-wrap gap-2 text-[10px] mt-1">
                          <span className="text-cyan-300">{inv.used_count}/{inv.max_uses === 0 ? "∞" : inv.max_uses} käyttöä</span>
                          <span className="text-white/50">vanhenee {inv.expires_at ? new Date(inv.expires_at).toLocaleString("fi-FI") : "—"}</span>
                          {inv.allow_rejoin && <span className="text-emerald-300/70 inline-flex items-center gap-0.5"><RefreshCw className="w-2.5 h-2.5" /> rejoin</span>}
                          {!inv.is_active && <span className="text-red-300">peruttu</span>}
                          {expired && <span className="text-amber-300">vanhentunut</span>}
                          {exhausted && <span className="text-amber-300">käytetty loppuun</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="outline" onClick={() => copyInviteLink(inv)} className="border-cyan-400/30 inline-flex items-center gap-1" data-testid={`invite-copy-${inv.id}`}>
                          <Copy className="w-3 h-3" />
                        </Button>
                        {inv.is_active && (
                          <Button size="sm" variant="ghost" onClick={() => revokeInvite(inv)} className="text-red-300" data-testid={`invite-revoke-${inv.id}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Tukipyyntö-dialogi */}
      <Dialog open={supportDialog} onOpenChange={setSupportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HeartHandshake className="w-5 h-5 text-pink-300" />
              Pyydä tukea pääkäyttäjältä
            </DialogTitle>
            <DialogDescription>
              Apps2Go pääkäyttäjä saa ilmoituksen ja liittyy kokoukseen mahdollisimman pian.
              Saat heti kutsulinkin, jonka kautta voit liittyä tukikokoukseen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Viesti (vapaaehtoinen)</Label>
              <textarea
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder="Kerro mihin tarvitset apua…"
                className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[100px]"
                data-testid="support-message"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSupportDialog(false)} className="flex-1" data-testid="support-cancel">
                Peruuta
              </Button>
              <Button
                onClick={sendSupportRequest}
                disabled={supportSending}
                className="btn-luxe flex-1 inline-flex items-center justify-center gap-2"
                data-testid="support-send"
              >
                {supportSending && <Loader2 className="w-3 h-3 animate-spin" />}
                <HeartHandshake className="w-3.5 h-3.5" /> Lähetä tukipyyntö
              </Button>
            </div>
            <p className="text-[10px] text-white/40 text-center">
              Tukipyyntö avaa kutsulinkin uudessa välilehdessä, jonka kautta liityt kokoukseen.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
